// StarRatingV2 – tek tetikleyici, portal popover, responsive yıldız boyutu,
// capture ile alttaki tıklamayı keser. Oy yazımı reputationClient.rateContent.
// Çalışmıyorsa bile event yayınlar (logda görürsün).

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import "./StarRatingV2.css";

import { auth } from "../../firebase";
import { rateContent, getMyRating } from "../../reputationClient";
import { getCachedMyRating } from "../../utils/myRatingsCache";
import { openStarInfo } from "../StarInfoSheet";
import { haptics } from "../../myclipsv3/design";
import { IkonKoprusu } from "../../icon-system/runtime/IkonKoprusu";
import { boyutPropsu } from "../../icon-system/adapters/boyut";

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));
const vw = () => (typeof window !== "undefined" ? window.innerWidth : 360);
const vh = () => (typeof window !== "undefined" ? window.innerHeight : 640);

interface StarProps {
  size?: number;
  active?: boolean;
  title?: string;
}

/**
 * FAZ C / DALGA 7 — 5★ YÜZEY TUTARLILIĞI (bayrak arkasında).
 *
 * Ölçülen kusur: aynı ürün sinyali (5★) iki yüzeyde İKİ AYRI çizimle görünüyordu —
 * MyClips rayı (`EngagementRailV3.tsx:903`) motor `rating.star` ailesini çiziyor,
 * ana akış tetiği ise bu dosyadaki yerel `Star()` svg'sini. Codemod bu iki çağrı
 * yerini (195, 418) atlamıştı çünkü `size` DİNAMİK (`size={icon}`, `size={size}`).
 *
 * Köprü çağrı yerlerine DEĞİL, `Star()`ın gövdesine kondu: iki çağrı yeri de
 * buradan geçiyor, `size` çözümü tek yerde kalıyor ve `active`/`title`/className
 * sözleşmesi DEĞİŞMİYOR (delta 0'ı ölçmek kolaylaşıyor).
 *
 * KORUNAN DAVRANIŞLAR (her biri `ikonDalga7Sozlesmesi.test.tsx`te AYRI ölçülür):
 *  · puan verme — tıklama `.sr2-item`/`.sr2-trigger` DÜĞMESİNDE; ikon dekoratif,
 *    hiçbir olay dinleyicisi taşımıyor → yol değişmedi.
 *  · devre dışı hâl — `disabled`/`disabledReason` tetik düğmesinde; ikona hiç
 *    uğramıyor ("Kendi gönderine puan veremezsin" metni ve `:disabled` görünümü aynı).
 *  · a11y — motor kolu DEKORATİF çizilir (`title` köprüye VERİLMEZ). Eski svg
 *    `aria-hidden="true"` + `role="img"` + `<title>` taşıyordu; `aria-hidden`
 *    kazandığı için ikonun ETKİN erişilebilir katkısı zaten YOKTU. `title`ı
 *    köprüye geçirmek IconFrame'i `role="img"`+`aria-label` yapar ve düğmenin
 *    kendi `aria-label`ıyla ÇİFT okuma üretirdi — yani "aynen koru" tam olarak
 *    bu: ikon sessiz kalır, adı düğme taşır.
 *  · KISMİ DOLGU — ÖLÇÜLDÜ: bu yüzeyde kısmi/yarım yıldız YOK. `active` ikili
 *    bir bayrak (`n <= shown`, `myVal > 0`); `sr2-gold` gradyanı DİKEY bir marka
 *    gradyanıdır (x1=y1=0 → x2=0,y2=1), oran göstergesi DEĞİL. Motor kolunda o
 *    gradyan `<defs>`i bulunmadığı için `fill:url(#sr2-gold)` çözülemez kalırdı
 *    (dolgu SİLİNİRDİ) → `StarRatingV2.css`te YALNIZ `[data-ikon]` gören bir kural
 *    dolu yıldızı rayla aynı altına (`#f4c430`) boyar. Bayrak kapalıyken o seçici
 *    hiçbir düğümle eşleşmez → piksel deltası 0.
 */
function Star({ size = 28, active = false, title }: StarProps) {
  return (
    <IkonKoprusu aile="rating.star" varyant={active ? "selected" : "outline"} {...boyutPropsu(size)} className={`sr2-star ${active ? "active" : ""}`} eski={<svg
      aria-hidden="true"
      role="img"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={`sr2-star ${active ? "active" : ""}`}
    >
      {title ? <title>{title}</title> : null}
      {/* EMİR #57 — PREMIUM ALTIN GRADYAN: dolu yıldız solid altın yerine
          dikey altın gradyan (Gold Star prestij dili). CSS .active path fill'i
          bu gradyanı referanslar (url(#sr2-gold)). */}
      <defs>
        <linearGradient id="sr2-gold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFE58A" />
          <stop offset="45%" stopColor="#FFD54A" />
          <stop offset="100%" stopColor="#F5A623" />
        </linearGradient>
      </defs>
      <path
        d="M12 2l2.955 6.201 6.844.996-4.95 4.826 1.169 6.817L12 17.77 5.982 20.84l1.169-6.817-4.95-4.826 6.844-.996L12 2z"
        fill="#fff"
        stroke="#111"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>} />
  );
}

/* --------------------------------- FEEDBACK -------------------------------- */
interface FeedbackProps {
  x: number;
  y: number;
  value: number;
  onDone: () => void;
}

function Feedback({ x, y, value, onDone }: FeedbackProps) {
  const left = clamp(x, 16, vw() - 16);
  const top = clamp(y, 24, vh() - 24);
  useEffect(() => {
    const t = setTimeout(onDone, 900);
    return () => clearTimeout(t);
  }, [onDone]);
  return ReactDOM.createPortal(
    <div className="sr2-feedback" style={{ left, top }}>
      {"★".repeat(value)}
      <span className="sr2-feedback-mul">×{value}</span>
    </div>,
    document.body
  );
}

/* --------------------------------- POPOVER --------------------------------- */
function useFitStarSize() {
  // 5 yıldız + 4 gap + yatay padding popover’a sığacak şekilde icon boyutunu düşür.
  const [size, setSize] = useState(30);
  useEffect(() => {
    const calc = () => {
      const maxWidth = Math.min(400, vw() - 32); // 16px sağ/sol tampon
      const gap = 6;
      const padX = 20; // 10px sağ + 10px sol
      let s = 30;
      // 5*s + 4*gap + padX <= maxWidth
      while (5 * s + 4 * gap + padX > maxWidth && s > 18) s -= 1;
      setSize(s);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);
  return size;
}

interface PopoverProps {
  x: number;
  y: number;
  onClose: () => void;
  onSelect: (n: number) => void;
  current?: number;
}

function Popover({ x, y, onClose, onSelect, current = 0 }: PopoverProps) {
  const icon = useFitStarSize();
  // ONARIM-4 (EMİR #39, bug#1): popover CSS'i translate(-50%) ile CENTER'a hizalı.
  // Eski clamp yalnız TETİK merkezini 16..vw-16'ya sıkıştırıyordu; popover
  // GENİŞLİĞİ hesaba katılmadığından kenardaki tetiklerde (feed rating rail'i
  // ekranın sağında) yıldızların yarısı ekran DIŞINA taşıyordu ("yıldızlar sola
  // kaydı"). Artık merkez, popover yarı-genişliği kadar içeri clamp'lenir →
  // 5 yıldız her zaman tam ekranda. (useFitStarSize içeriği vw-32'ye sığdırır,
  // dolayısıyla clamp aralığı hiç ters dönmez.)
  const halfW = (5 * icon + 4 * 6 + 20) / 2 + 4; // 5 ikon + 4 gap + yatay padding + kenarlık
  const left = clamp(x, halfW + 8, vw() - halfW - 8);
  const top = clamp(y, 56, vh() - 16);
  // PR-30 — açılışta kullanıcının MEVCUT puanı dolu gösterilir (hover
  // olmadığında current highlight'ı görünür kalır → "kendi yıldızım").
  const [hover, setHover] = useState(0);
  const shown = hover || current;
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const stopAll = (e: any) => {
    if (!e) return;
    if (e.cancelable) e.preventDefault();
    e.stopPropagation();
    if (e.nativeEvent?.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation();
  };

  // Parmağı panel üzerinde gezdirirken hover güncelle (mobilde "hover yok" sorununu çözer)
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const box = wrapRef.current?.getBoundingClientRect();
    if (!box) return;
    const relX = clamp(e.clientX - box.left, 0, box.width);
    const step = box.width / 5;
    const h = clamp(Math.ceil(relX / step), 1, 5);
    setHover(h);
  };

  const node = (
    <>
      <div className="sr2-backdrop" onMouseDownCapture={stopAll} onClick={onClose} />
      <div
        ref={wrapRef}
        className="sr2-popover"
        style={{ left, top }}
        role="dialog"
        aria-label="Puan seç"
        /* ── ODAK TUZAĞI UZANTISI (GÖZ klavye turu, 2026-08-29) ────────────
           Bu balon `document.body`ye portal'lanıyor; bir modalın İÇİNDEN
           açıldığında modalın odak tuzağının KABI DIŞINDA kalıyor. Tuzak
           kabın son öğesinde sekmeyi başa sardığı için tarayıcı buraya HİÇ
           ulaşamıyordu → 5★ fareyle yapılabiliyor, KLAVYEYLE YAPILAMIYORDU
           (WCAG 2.1.1). Ölçüm: `10-feed-rate` sekme halkasında
           `sr2-item«1»…«5»` ve `sr2-infolink` YOKTU.
           Bu öznitelik `useFocusTrap`e "bu düğümler mantıksal olarak katmana
           ait" der. Opt-in — işareti taşımayan portal'lar etkilenmez; tuzaksız
           bağlamda da zararsız (okuyan yok). KALDIRMA → erişim geri gider. */
        data-odak-uzantisi=""
        onMouseDownCapture={stopAll}
        onClick={stopAll}
        onPointerMove={onPointerMove}
      >
        {/* PR-54 — "Benim puanım" etiketi. current (=myVal) popover açılınca
            ZATEN çekildi → ekstra read YOK, feed'de N+1 YOK. hover değil current
            gösterilir (verdiğin puan sabit kalır). */}
        <div className="sr2-mine">
          {current > 0 ? `Puanın: ${current}★ · değiştirebilirsin` : "Puanını seç (1–5)"}
        </div>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className="sr2-item pressable"
            aria-label={`${n} yıldız`}
            onMouseEnter={() => setHover(n)}
            onFocus={() => setHover(n)}
            onClick={(e) => {
              stopAll(e);
              onSelect(n);
            }}
          >
            <Star size={icon} active={n <= shown} />
            <span className="sr-only">{n}</span>
          </button>
        ))}
        {/* PR-52 — "5★ nedir?" anlatım linki (rating write davranışını
            değiştirmez; popover'ı kapatıp açıklama sheet'ini açar). */}
        <button
          type="button"
          className="sr2-infolink"
          onClick={(e) => { stopAll(e); onClose(); openStarInfo(); }}
        >
          5★ nedir?
        </button>
      </div>
    </>
  );

  return ReactDOM.createPortal(node, document.body);
}

/* --------------------------------- ROOT ------------------------------------ */
/**
 * Props:
 *  - contentId, authorId, type: "post" | "clip" | "story"
 *  - className, size, disabled
 *  - onRated?(value:number)
 */
interface StarRatingV2Props {
  contentId?: string;
  authorId?: string;
  type?: string;
  className?: string;
  size?: number;
  disabled?: boolean;
  /**
   * #931 — TIKLANAMAYAN TETİK SEBEBİNİ SÖYLER.
   * `disabled` tek başına sessizdir: kullanıcı yıldızı görür, basar, hiçbir
   * şey olmaz ve NEDEN olmadığını öğrenemez. Bu prop verilirse erişilebilir
   * ad (`aria-label`) + `title` sebebi taşır (ör. kendi gönderin).
   */
  disabledReason?: string;
  /**
   * Puan BAŞARIYLA işlendikten sonra çağrılır. Promise dönerse BEKLENİR —
   * #931: eskiden `onRated(val)` await EDİLMİYORDU, bu yüzden delege yazım
   * yolunun (`usePostLogic.handleRate` gibi) reddi `select`in kendi
   * `catch`inin DIŞINA düşüyor ve YAKALANMAMIŞ sayfa hatası oluyordu
   * (ölçülen imzalar: `PERMISSION_DENIED` öz-puanlamada, `RATING_COOLDOWN`
   * re-rate'te).
   */
  onRated?: (value: number) => void | Promise<void>;
  // Boot/agent katmanı ekstra prop geçebilir (openAt, onRate vb.)
  [key: string]: any;
}

interface FeedbackState {
  x: number;
  y: number;
  val: number;
}

export default function StarRatingV2({
  contentId,
  authorId,
  type = "post",
  className = "",
  size = 28,
  disabled = false,
  disabledReason = "",
  onRated,
  // PR-29 — dıştan popover açtırma (çift-dokun karar uyumu): nonce her
  // değiştiğinde popover trigger konumundan açılır. Opsiyonel; mevcut
  // tüketiciler etkilenmez.
  openNonce = 0,
}: StarRatingV2Props) {
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [fb, setFb] = useState<FeedbackState | null>(null); // {x,y,val}
  /* Öz-puanlama sebebinin ETKİLEŞİM ANINDAKİ kanalı (2026-09-13, owner kararı:
     *"Kalıcı açıklamayı gönderi akışından çıkar; puanlama yasağını koru ve
     gerekli açıklamayı ilgili etkileşimde erişilebilir biçimde göster."*).
     Eskiden sebep her kartta KALICI bir metin satırıydı (4 yüzeyde). Artık
     tetik `aria-disabled` (odaklanabilir + basılabilir) ve basınca sebep hem
     bu canlı bölgeye hem toast'a düşer. HTML `disabled` KALDIRILDI, çünkü
     devre dışı düğme hiç olay almaz — "basınca açıkla" o hâlde imkânsızdı.
     ⚠️ YAZMA YASAĞI DEĞİŞMEDİ: `select()` ve `openFromTrigger` erken döner
     (popover hiç açılmaz), sunucu kuralı (firestore.rules `authorId != uid`)
     zaten kilit. Değişen tek şey açıklamanın YERİ. */
  const [duyuru, setDuyuru] = useState<{ metin: string; sesli: boolean }>({ metin: "", sesli: false });
  // PR-30 — kullanıcının bu içeriğe verdiği MEVCUT puan. N+1 KORUMASI:
  // mount'ta OKUNMAZ; yalnız popover İLK açıldığında lazy çekilir (fetchedRef
  // tek-sefer guard) + seçim sonrası local güncellenir. Trigger dolu görünür.
  // PR-63 — ilk değer cihaz-kalıcı cache'ten (contentId prop'u verilen
  // yüzeylerde trigger İLK render'da dolu görünür; Firestore okuması YOK).
  // Popover açılınca canonical getMyRating aynen çalışır ve cache'i düzeltir.
  const [myVal, setMyVal] = useState(
    () => getCachedMyRating(auth?.currentUser?.uid, contentId) ?? 0,
  );
  const fetchedRef = useRef(false);

  // Popover açılınca (trigger VEYA openNonce) mevcut puanı bir kez çek.
  const ensureMyRating = useCallback(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    if (!contentId) return;
    getMyRating(contentId).then((v) => {
      if (v) setMyVal(v);
    }).catch(() => { /* sessiz — puan yoksa 0 kalır */ });
  }, [contentId]);

  const stopAll = useCallback((e: any) => {
    if (!e) return;
    // touch* event'leri React root'ta PASSIVE bağlanır — preventDefault
    // çağrılamaz (konsolda "Unable to preventDefault" basar). Link/anchor
    // iptali görevini pointerdown/mousedown zaten üstleniyor.
    const isTouch = typeof e.type === "string" && e.type.startsWith("touch");
    if (!isTouch && e.cancelable) e.preventDefault(); // alttaki link/anchor’ı iptal
    e.stopPropagation();
    if (e.nativeEvent?.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation();
  }, []);

  /** Devre dışı tetiğe basıldı → sebebi SÖYLE (canlı bölge + toast).
   *  Popover açılmaz, hiçbir yazma denenmez. */
  const sebebiDuyur = useCallback(() => {
    if (!disabledReason) return;
    /* TEK SES KANALI (owner, 2026-09-13: *"Aynı açıklamayı toast ve canlı
       bölge üzerinden ekran okuyucuya İKİ KEZ okutma."*). ÖLÇÜLDÜ:
       `ToastBoot.tsx:38` kabı zaten `role="status" aria-live="polite"` —
       yani toast'ın kendisi ekran okuyucuya konuşur. Bu yüzden gizli metin
       VARSAYILAN OLARAK SESSİZDİR (rol/aria-live YAZILMAZ); yalnız toast
       yüklenemezse (`catch`) sesli hâle geçer ve tek duyuru yine olur. */
    setDuyuru({ metin: disabledReason, sesli: false });
    import("../../ToastBoot")
      .then(({ showToast }) => showToast(disabledReason))
      .catch(() => setDuyuru({ metin: disabledReason, sesli: true }));
  }, [disabledReason]);

  const openFromTrigger = useCallback(
    (e: any) => {
      stopAll(e);
      if (disabled) { sebebiDuyur(); return; }
      const r = btnRef.current?.getBoundingClientRect?.();
      if (!r) return;
      ensureMyRating();
      setPos({ x: Math.round(r.left + r.width / 2), y: Math.round(r.top) });
      setOpen(true);
    },
    [disabled, sebebiDuyur, stopAll, ensureMyRating]
  );

  const close = useCallback(() => setOpen(false), []);

  // PR-29 — openNonce ile programatik açılış (çift-dokun → popover).
  const lastNonceRef = useRef(0);
  useEffect(() => {
    if (!openNonce || openNonce === lastNonceRef.current) return;
    lastNonceRef.current = openNonce;
    if (disabled) return;
    const r = btnRef.current?.getBoundingClientRect?.();
    if (!r) return;
    ensureMyRating();
    setPos({ x: Math.round(r.left + r.width / 2), y: Math.round(r.top) });
    setOpen(true);
  }, [openNonce, disabled, ensureMyRating]);

  const select = useCallback(
    async (val: number) => {
      if (disabled || busy) return;
      setOpen(false);

      // EMİR #57 — commit'te premium medium haptik (RateStarsV3 paritesi).
      try { haptics.medium(); } catch (_) {}

      // feedback (balon) – tetik noktasının biraz üstünden başlat
      const r = btnRef.current?.getBoundingClientRect?.();
      if (r) setFb({ x: r.left + r.width / 2, y: Math.max(24, r.top - 8), val });

      try {
        const uid = auth?.currentUser?.uid;
        if (uid && contentId && authorId) {
          setBusy(true);
          await rateContent({ contentId, authorId, value: val, type });
          // PR-30 — başarılı yazımda kendi puanımı local güncelle (popover
          // tekrar açılınca dolu görünür; fetchedRef zaten set → yeniden okumaz).
          setMyVal(val);
        } else {
          // dev
          window.dispatchEvent(new CustomEvent("mylasa:rate", { detail: { value: val } }));
          // eslint-disable-next-line no-console
        }
        // #931 — `await`: `onRated` bir YAZIM yapıyor olabilir (delege yol).
        // Await edilmezse dönen promise'in reddi bu `try`ın DIŞINA düşer ve
        // yakalanmamış sayfa hatası olur. Sync callback'ler etkilenmez.
        await onRated?.(val);
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error("[StarRatingV2] rateContent hata:", err);
        // PR-30 — hata artık SESSİZ değil. Cooldown toast'ı reputationClient'tan
        // zaten gelir; diğer hatalarda (permission/network) kullanıcıya bildir.
        if (err?.code !== "rating-cooldown") {
          try {
            const { showToast } = await import("../../ToastBoot");
            showToast("Puan kaydedilemedi. Tekrar dener misin?", { variant: "error" });
          } catch { /* toast yoksa sessiz */ }
        }
      } finally {
        setBusy(false);
      }
    },
    [authorId, busy, contentId, disabled, onRated, type]
  );

  // FEED ALT BARI İÇİN: capture + pointerdown + click -> tüm kontekslerde çalışır
  const handlers = useMemo(
    () => ({
      onMouseDownCapture: stopAll,
      onTouchStartCapture: stopAll,
      onPointerDownCapture: stopAll,
      onPointerDown: openFromTrigger, // mouse & touch anında aç
      onClick: openFromTrigger, // bazı yapılar pointerdown'u bastırabiliyor
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") openFromTrigger(e);
      },
    }),
    [openFromTrigger, stopAll]
  );

  // #931 — devre dışıysa ve SEBEP verildiyse, erişilebilir ad sebebi taşır.
  // "Yıldız ver" yazan ama basılınca hiçbir şey yapmayan bir tetik, sessiz
  // bir çıkmazdır; sebep hem ekran okuyucuya hem `title`a girer.
  const etiket = disabled && disabledReason
    ? disabledReason
    : myVal > 0
      ? `Puanın: ${myVal} yıldız`
      : "Yıldız ver";

  return (
    <div className={`sr2-wrap ${className}`} data-sr2="wrap">
      <button
        ref={btnRef}
        type="button"
        className={`sr2-trigger${myVal > 0 ? " sr2-rated" : ""}`}
        aria-label={etiket}
        title={etiket}
        data-sr2-disabled-reason={disabled && disabledReason ? disabledReason : undefined}
        /* ⊘ §5 AÇIĞI ÖLÇÜLDÜ, BU TURDA KAPATILMADI — gerekçesi kayıtlı.
           `docs/ikon/KULLANIM_SOZLESMESI.md` §5: *"devre dışı → opaklık düşer
           VE `aria-disabled="true"` olur. Biri varken öteki yoksa bu bir
           kusurdur."* Bugün opaklık VAR (`.sr2-trigger:disabled{opacity:.4}`),
           öznitelik YOK → sözleşmeye göre kusur.
           NİYE BURADA KAPATILMADI: `aria-disabled` eklendiğinde
           `src/__tests__/ikonDalga7Sozlesmesi.test.tsx` §(i) KIRMIZI verdi —
           o kapı bu dosyanın bayrak-KAPALI kolunun sabit bir git çapasına
           BAYT olarak eşit kalmasını istiyor ve muafiyet tablosu tek amaçlı
           (o gün tek girdisi "Yerinde" yamasıydı; 2026-09-13 §4 ortak
           çözümüyle — `IconFrame satirIci` — o yama söküldü ve tablo BOŞ.
           Buraya girdi eklemek kapının KENDİSİNİ değiştirmek olurdu = gevşetme).
           Ayrıca açık TEK yüzeyde değil: aynı eksik `RateStarsV3` ve klip
           yüzeylerinde de var; yalnız burayı düzeltmek kısmi kapsam üretirdi.
           → Durum dili turuna devredildi; çapanın yeniden konması o turun işi. */
        /* `aria-disabled` (HTML `disabled` DEĞİL) — 2026-09-13, owner kararı.
           Devre dışı bir düğme olay ALMAZ; sebebi "basınca açıklamak" ancak
           tetik basılabilir kalırsa mümkün. Görünüm ve ekran-okuyucu hâli
           aynen korunur (`.sr2-trigger[aria-disabled="true"]` → opacity .4 +
           not-allowed), üstelik KULLANIM_SOZLESMESI §5'in "opaklık var ama
           `aria-disabled` yok" açığı da bu turda kapandı. `busy` gerçek
           `disabled` kalır: o geçici bir yazma kilididir, açıklanacak bir
           kural değil. */
        aria-disabled={disabled || undefined}
        disabled={busy}
        {...handlers}
      >
        {/* PR-30 — kendi puanım varsa trigger dolu (active) görünür. */}
        <Star size={size} active={myVal > 0} title={etiket} />
      </button>

      {/* Sebep KANALI — yalnız basıldığında dolar, yer kaplamaz (1×1, clip-path).
          Ekran okuyucuya normalde TOAST konuşur (o kap `role="status"`); bu
          düğüm sessiz kalır ki aynı cümle iki kez okunmasın. Toast
          yüklenemezse `sesli` olur ve tek duyuruyu bu üstlenir. */}
      <span
        className="sr2-duyuru"
        role={duyuru.sesli ? "status" : undefined}
        aria-live={duyuru.sesli ? "polite" : undefined}
      >
        {duyuru.metin}
      </span>

      {open && <Popover x={pos.x} y={pos.y} onClose={close} onSelect={select} current={myVal} />}

      {fb && (
        <Feedback
          x={fb.x}
          y={fb.y}
          value={fb.val}
          onDone={() => setFb(null)}
        />
      )}
    </div>
  );
}
